export type NotificationPersona = 'coach' | 'rower' | 'parent' | 'admin';

export interface AppNotification {
  id: string;
  eventType: string;
  title: string;
  body: string;
  deepLink?: string | null;
  createdAt: string;
  readAt: string | null;
  status: 'unread' | 'read';
}

interface NotificationsResponse {
  items?: AppNotification[];
}

async function throwApiError(response: Response, fallbackMessage: string): Promise<never> {
  const body = await response.json().catch(() => ({}));
  throw new Error((body as { error?: string }).error || fallbackMessage);
}

function apiBase(): string {
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  return isLocalhost ? 'http://localhost:3001' : '';
}

export async function fetchNotifications(
  token: string,
  persona: NotificationPersona
): Promise<AppNotification[]> {
  const response = await fetch(
    `${apiBase()}/api/notifications?persona=${encodeURIComponent(persona)}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    return throwApiError(response, 'Failed to load notifications');
  }

  const data = (await response.json()) as NotificationsResponse;
  return (data.items || []).map((item) => ({
    ...item,
    status: item.readAt ? 'read' : 'unread',
  }));
}

export async function fetchUnreadCount(
  token: string,
  persona: NotificationPersona
): Promise<number> {
  const response = await fetch(
    `${apiBase()}/api/notifications/unread-count?persona=${encodeURIComponent(persona)}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    return throwApiError(response, 'Failed to load unread notification count');
  }

  const data = (await response.json()) as { count?: number };
  return data.count ?? 0;
}

export async function markNotificationRead(token: string, id: string): Promise<void> {
  const response = await fetch(`${apiBase()}/api/notifications/read`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id }),
  });

  if (!response.ok) {
    return throwApiError(response, 'Failed to mark notification read');
  }
}

export async function archiveNotification(token: string, id: string): Promise<void> {
  const response = await fetch(`${apiBase()}/api/notifications/archive`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id }),
  });

  if (!response.ok) {
    return throwApiError(response, 'Failed to remove notification');
  }
}
